import { Boxes, GitBranch, Loader2, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { RepoEntry } from '../api/types.js';
import { DriftBadge } from '../components/drift-badge.js';
import { cn } from '../lib/cn.js';

export interface RepoCardProps {
  readonly repo: RepoEntry;
  readonly reindexing: boolean;
  readonly onReindex: () => void;
}

export function RepoCard({ repo, reindexing, onReindex }: RepoCardProps): JSX.Element {
  return (
    <li className="group relative overflow-hidden rounded-2xl border border-border-subtle bg-surface/80 p-5 transition-all hover:border-accent/40 hover:bg-surface hover:shadow-glow-soft">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden
      />
      <div className="flex items-center gap-5">
        <div className="grid h-12 w-12 flex-none place-items-center rounded-xl border border-border-subtle bg-deep text-node-folder">
          <Boxes className="h-5 w-5" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-3">
            <Link
              to={`/repos/${repo.slug}`}
              className="truncate text-lg font-semibold text-text-primary transition-colors hover:text-accent"
            >
              {repo.displayName}
            </Link>
            <DriftBadge status={repo.lastRefreshStatus} lastIndexedAt={repo.lastIndexedAt} />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] text-text-muted">
            <span>{repo.slug}</span>
            <span className="text-border-default">·</span>
            <span className="inline-flex items-center gap-1">
              <GitBranch className="h-3 w-3" />
              {repo.defaultBranch}
            </span>
            <span className="text-border-default">·</span>
            <span>{repo.lastCommit ? repo.lastCommit.slice(0, 8) : '—'}</span>
          </div>
        </div>
        <button
          type="button"
          disabled={reindexing}
          onClick={onReindex}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium transition-all',
            reindexing
              ? 'cursor-wait border-border-subtle bg-surface text-text-muted'
              : 'border-border-subtle bg-elevated text-text-secondary hover:border-accent/40 hover:bg-accent/10 hover:text-text-primary',
          )}
        >
          {reindexing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCw className="h-3.5 w-3.5" />
          )}
          Reindex
        </button>
      </div>
    </li>
  );
}
